// src/screens/LoanDrilldownScreen/tabs/LoanDrilldownFilesTab/LoanFilesTable/LoanFilesTable.jsx
import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MultiFilterModule,
  PivotModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  SideBarModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import size from "lodash/size";

import AgGridTableWrapper from "../../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";

import NumberInput from "../../../../../_src_shared/components/inputs/NumberInput";
import DateInput from "../../../../../components/inputs/DateInput";
import InputWrapper from "../../../../../components/inputs/shared/InputWrapper";

import { CheckCircleOutline } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import CheckboxInput from "../../../../../_src_shared/components/inputs/CheckboxInput";
import SelectAutoInput from "../../../../../components/inputs/SelectAutoInput";
import SelectToggleInput from "../../../../../components/inputs/SelectToggleInput";
import PinnedModal from "../../../../../components/modals/PinnedModal";
import { LOAN_FILES_TABLE_LAYOUT_TYPES } from "../../../../../constants/enums/LOAN_FILES_TABLE_LAYOUT_TYPES";
import { selectOptionsDocumentStatus } from "../../../../../constants/selectOptions/selectOptionsDocumentStatus";
import { useFileDocGroupAndTypesHook } from "../../../../../hooks/useFileDocGroupAndTypesHook";
import { filePreviewSetData } from "../../../../../redux/reducers/filePreviewReducer";
import {
  useAutoGroupColumnDef,
  useColumnDefs,
  useColumnTypesLoanFilesTable,
  useGetRowId,
  useHandleCellValueChanged,
  useHandleDeselectAllRows,
  useHandleMetadataUpdate,
  useHandleRowSelectionChange,
  useRowSelection,
} from "./hooks";

ModuleRegistry.registerModules([
  SideBarModule,
  RowGroupingPanelModule,
  MultiFilterModule,
  RowGroupingModule,
  PivotModule,
  TreeDataModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  SetFilterModule,
]);

const LoanFilesTable = ({
  rowData,
  setRowData,
  editing,
  selectedRows,
  setSelectedRows,
  assigning,
  selectable,
  // groupByStatus,
  // groupByCategory,
  layoutMode,
  quickFilter,
  setChangedRows,
  sortFilter,
  freezePanes,
  onEditClick,
  hideDocGroupColumn,
  hideDocTypeColumn,
}) => {
  const gridRef = useRef(null);
  const { fileDocGroups, getDocTypesByGroup } = useFileDocGroupAndTypesHook();
  const originalRowDataRef = useRef(rowData);
  const originalRowData = originalRowDataRef.current;
  const dispatch = useDispatch();
  // Column definitions & types
  const columnDefs = useColumnDefs({
    editing: editing && !selectable,
    layoutMode,
    // groupByCategory,
    // groupByStatus,
    sortFilter,
    freezePanes,
    hideDocGroupColumn,
    hideDocTypeColumn,
    fileDocGroups,
    getDocTypesByGroup,
  });
  const columnTypes = useColumnTypesLoanFilesTable();
  const getRowId = useGetRowId();
  const rowSelection = useRowSelection(editing || selectable);
  // const rowSelection = useRowSelection(editing);
  const autoGroupColumnDef = useAutoGroupColumnDef({ layoutMode });

  // Handle row selection changes
  const handleRowSelectionChange = useHandleRowSelectionChange(setSelectedRows);

  // Deselect all rows callback (no longer resets data)
  const handleDeselectAllRows = useHandleDeselectAllRows(
    setSelectedRows,
    gridRef,
  );

  // When a single cell changes
  const handleCellValueChanged = useHandleCellValueChanged(
    originalRowData,
    setChangedRows,
  );

  // Bulk‐update metadata callback
  const handleMetadataUpdate = useHandleMetadataUpdate(
    selectedRows,
    originalRowData,
    setRowData,
    setChangedRows,
    gridRef,
  );

  const onRowClicked = useCallback((params) => {
    if (params.node.group) {
      params.node.setExpanded(!params.node.expanded);
    }
  }, []);

  const onCellClicked = useCallback(
    (params) => {
      if (params?.colDef?.field === "file_display_name" && !editing) {
        dispatch(filePreviewSetData(params.data));
      }
    },
    [editing],
  );

  const selectionColumnDef = useMemo(() => {
    return {
      sortable: true,
      // resizable: true,
      width: 48,
      suppressHeaderMenuButton: false,
      pinned: "left",
      resizable: false,
    };
  }, []);

  const getContextMenuItems = useCallback(() => {
    const result = [
      {
        name: "Edit Files",
        action: () => {
          onEditClick(); // Call the edit function from parent
        },
        icon: `<svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium  css-16j6ggo-MuiSvgIcon-root" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="EditOutlinedIcon"><path d="m14.06 9.02.92.92L5.92 19H5v-.92zM17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z"></path></svg>`,
        disabled: editing, // Disable if already editing
      },
      "separator",
      "copy",
      "copyWithHeaders",
      "copyWithGroupHeaders",
      "separator",
      "export",
    ];

    return result;
  }, [onEditClick, editing]);

  // const getRowStyle = useGetRowStyles();

  if (isEmpty(rowData)) {
    return (
      <Box sx={{ p: 3 }}>
        <Txt secondary>No files uploaded</Txt>
      </Box>
    );
  }

  return (
    <Flx
      flexGrow={1}
      column
      gap={0}
      sx={
        {
          // maxHeight: "90vh",
          // overflowY: "auto",
          //
        }
      }
    >
      <Flx fw jb ac>
        {isEmpty(selectedRows) || selectable ? null : (
          <BulkActionsToolbar
            selectedRows={selectedRows}
            onDeselectAllRows={handleDeselectAllRows}
            onMetadataUpdate={handleMetadataUpdate}
          />
        )}
      </Flx>

      <Box
        column
        sx={{
          // overflow: "auto",
          // maxHeight: "89vh",
          // height: "100%",
          gap: 2,
          ".ag-row-selected:before": {
            backgroundColor: "#e4f2fd !important",
          },
          ".ag-row-selected .ag-cell": {
            fontWeight: "600 !important",
          },
        }}
      >
        <AgGridTableWrapper
          // height="500px"
          // height="100%"
          suppressMinHeightWhenPopulated={rowData}
          simpleTable
          size={4}
          sx={{
            // maxHeight: "80vh",
            width: "100%",
            height: "100%",

            //
          }}
        >
          <AgGridReact
            ref={gridRef}
            getRowId={getRowId}
            rowData={rowData}
            columnDefs={columnDefs}
            columnTypes={columnTypes}
            singleClickEdit
            quickFilterText={quickFilter}
            stopEditingWhenCellsLoseFocus
            onRowClicked={onRowClicked}
            autoGroupColumnDef={autoGroupColumnDef}
            onCellClicked={onCellClicked}
            // rowHeight={50}
            defaultColDef={{
              flex: 1,
              minWidth: 150,
            }}
            onSelectionChanged={handleRowSelectionChange}
            onCellValueChanged={handleCellValueChanged}
            rowSelection={rowSelection}
            groupDefaultExpanded={
              layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.CATEGORY_AND_TYPE
                ? 1
                : 0
            }
            selectionColumnDef={selectionColumnDef}
            // onFirstDataRendered={onFirstDataRendered}
            // getRowStyle={getRowStyle}
            groupDisplayType={"groupRows"}
            domLayout={"autoHeight"}
            tooltipShowDelay={50}
            tooltipHideDelay={3000}
            tooltipMouseTrack
            // tooltipInteraction
            getContextMenuItems={getContextMenuItems} // Add context menu
            allowContextMenuWithControlKey={true} // Allow context menu with Ctrl+right-click
            suppressContextMenu={false} // Enable context menu
          />
        </AgGridTableWrapper>
      </Box>
    </Flx>
  );
};

const BulkActionsToolbar = ({
  selectedRows,
  onDeselectAllRows,
  onMetadataUpdate,
}) => {
  // State for all fields
  const [fieldValues, setFieldValues] = useState({
    documentStatus: null,
    docGroup: null,
    docType: null,
    reportEffectiveDate: "",
    reportGuidelineExpirationDays: null,
    isHidden: false,
    // Future fields can be easily added here
    // exampleFutureField: null,
  });

  const { fileDocGroups, getDocTypesByGroup } = useFileDocGroupAndTypesHook();
  // Generic field update handler
  const updateField = useCallback(
    (fieldName, value) => {
      setFieldValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      // Call the metadata update with the specific field
      onMetadataUpdate({ [fieldName]: value });
    },
    [onMetadataUpdate],
  );

  // Specific handlers for each field type
  const onDocumentStatusChange = useCallback(
    (status) => {
      updateField("documentStatus", status);
    },
    [updateField],
  );

  // const onDocGroupTypeChange = useCallback(
  //   (docGroup, docType) => {
  //     // Update both fields simultaneously
  //     setFieldValues((prev) => ({
  //       ...prev,
  //       docGroup,
  //       docType,
  //     }));

  //     // Send both updates together
  //     onMetadataUpdate({
  //       newDocGroup: docGroup,
  //       newDocType: docType,
  //     });
  //   },
  //   [onMetadataUpdate]
  // );

  const onDocGroupChange = useCallback(
    (group) => {
      setFieldValues((prev) => ({
        ...prev,
        docGroup: group,
        docType: "", // Clear docType when group changes
      }));
      onMetadataUpdate({ newDocGroup: group });
    },
    [onMetadataUpdate],
  );

  const onDocTypeChange = useCallback(
    (type) => {
      updateField("docType", type);
    },
    [updateField],
  );

  const onEffectiveDateChange = useCallback(
    (date) => {
      updateField("reportEffectiveDate", date);
    },
    [updateField],
  );

  const onNumExpirationDaysChange = useCallback(
    (num) => {
      updateField("reportGuidelineExpirationDays", num);
    },
    [updateField],
  );

  // const onFilenameChange = useCallback(
  //   (num) => {
  //     //                  const originalExtension = data.name.split(".").pop().toLowerCase();
  //     // const newValueWithoutExt = newValue.replace(/\.[^/.]+$/, "");
  //     // data.file_display_name = `${newValueWithoutExt}.${originalExtension}`;

  //     // params.api.applyTransaction({ update: [data] });

  //     // const origValue = originalRow.file_display_name;
  //     updateField("file_display_name", num);
  //   },
  //   [updateField]
  // );

  const onIsHiddenChange = useCallback(
    (isHidden) => {
      updateField("isHidden", isHidden);
    },
    [updateField],
  );

  // Generic handler for future fields

  // Populate fields when exactly one row is selected
  useEffect(() => {
    if (size(selectedRows) === 1) {
      const file = selectedRows[0];
      setFieldValues({
        documentStatus: file.documentStatus || null,
        docGroup: file.docGroup || null,
        docType: file.docType || null,
        reportEffectiveDate: file.reportEffectiveDate || "",
        reportGuidelineExpirationDays:
          file.reportGuidelineExpirationDays || null,
        isHidden: file.isHidden || false,
        // Future fields would be added here
        // exampleFutureField: file.exampleFutureField || null,
      });
      return;
    }

    // Clear all fields when multiple or no rows selected
    setFieldValues({
      documentStatus: null,
      docGroup: null,
      docType: null,
      reportEffectiveDate: "",
      reportGuidelineExpirationDays: null,
      isHidden: false,
      // exampleFutureField: null,
    });
  }, [selectedRows]);

  if (isEmpty(selectedRows)) {
    return null;
  }

  return (
    <PinnedModal
      show={true}
      title={`Edit Rows (${size(selectedRows)})`}
      height="620px"
      icon={<CheckCircleOutline className="thin" />}
      maxWidth="540px"
      onClose={onDeselectAllRows}
    >
      <Flx column gap={4} sx={{ flexGrow: 1, p: 2, pb: 0 }}>
        {/* Document Status */}

        <InputWrapper sx={{ minWidth: "280px" }} label="Status">
          <SelectToggleInput
            value={fieldValues.documentStatus}
            placeholder="Select Status"
            options={selectOptionsDocumentStatus}
            onChange={onDocumentStatusChange}
          />
        </InputWrapper>
        {/* <InputWrapper label="File Display Name">
          <TextInput
            value={fieldValues.file_display_name}
            placeholder="File Display Name"
            onChange={onFilenameChange}
          />
        </InputWrapper> */}
        {/* Combined Document Group & Type */}
        {/* <InputWrapper
          sx={{ minWidth: "280px" }}
          label="Document Category & Type"
        >
          <SelectFileAssignmentInput
            placeholder="Search document categories and types..."
            onChange={(params) =>
              onDocGroupTypeChange(params?.docGroup, params?.docType)
            }
            docGroup={fieldValues.docGroup}
            docType={fieldValues.docType}
            fullWidth
          />
        </InputWrapper> */}

        {/* Individual Document Group & Type Dropdowns */}

        <InputWrapper label="Category">
          <SelectAutoInput
            value={fieldValues.docGroup}
            placeholder="Category"
            options={fileDocGroups}
            onChange={onDocGroupChange}
          />
        </InputWrapper>
        {isNil(fieldValues.docGroup) ? null : (
          <InputWrapper label="Category Type">
            <SelectAutoInput
              value={fieldValues.docType}
              placeholder="Select Category Type"
              options={getDocTypesByGroup(fieldValues.docGroup) || []}
              onChange={onDocTypeChange}
            />
          </InputWrapper>
        )}
        {/* <InputWrapper label="Category Type" sx={{ minWidth: "280px" }}>
          {isNil(fieldValues.docGroup) ? (
            <SelectAutoInput
              value={fieldValues.docType}
              placeholder="Category Type Required"
              options={getDocTypesByGroup(fieldValues.docGroup) || []}
              onChange={onDocTypeChange}
              disabled={
                isNil(fieldValues.docGroup) ||
                isEmpty(getDocTypesByGroup(fieldValues.docGroup))
              }
            />
          ) : (
            <SelectAutoInput
              value={fieldValues.docType}
              placeholder="Select Category Type"
              options={getDocTypesByGroup(fieldValues.docGroup) || []}
              onChange={onDocTypeChange}
            />
          )}
        </InputWrapper> */}

        {/* Report Effective Date & Expiration Days */}
        <Flx gap={2}>
          <InputWrapper fullWidth label="Report Effective Date">
            <DateInput
              value={fieldValues.reportEffectiveDate}
              placeholder="Effective Date"
              onChange={onEffectiveDateChange}
            />
          </InputWrapper>

          <InputWrapper fullWidth label="Report Guideline Expiration Days">
            <NumberInput
              value={fieldValues.reportGuidelineExpirationDays}
              placeholder="Exp. Days"
              onChange={onNumExpirationDaysChange}
            />
          </InputWrapper>
        </Flx>
        <InputWrapper fullWidth label="Hide Files">
          <SelectToggleInput
            value={fieldValues.isHidden}
            // placeholder="Exp. Days"
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
            onChange={onIsHiddenChange}
          />
        </InputWrapper>

        {/* Boolean Fields */}
        <Flx column>
          <CheckboxInput
            label={"Approved For Third Party"}
            checked={fieldValues.approvedForThirdParty}
            onChange={(v) => updateField("approvedForThirdParty", v)}
          />
          <CheckboxInput
            label={"Exclude From Export"}
            checked={fieldValues.excludeFromExport}
            onChange={(v) => updateField("excludeFromExport", v)}
          />
        </Flx>
        {/* Future fields section - easily extensible */}
        {/* <InputWrapper fullWidth label="New Future Field">
          <DateInput
            value={fieldValues.exampleFutureField}
            placeholder="New Future Field"
            onChange={onFieldChange('exampleFutureField')}
          />
        </InputWrapper> */}
      </Flx>
    </PinnedModal>
  );
};

export default LoanFilesTable;
